import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { User, Lock, Building, Mail, Check, Loader2, CreditCard, ExternalLink, Sparkles, Shield, Download, Trash2, Smartphone, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const { user, setUser, refreshUser } = useAuth();

  // Profile state
  const [name, setName] = useState(user?.name || "");
  const [company, setCompany] = useState(user?.company || "");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  // Password state
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  const handleProfileSave = async () => {
    setProfileSaving(true);
    setProfileMsg(null);
    try {
      const { data } = await axios.put(`${API}/auth/profile`, { name, company }, { withCredentials: true });
      setUser(data);
      setProfileMsg({ type: "success", text: "Profile updated successfully" });
    } catch (err) {
      setProfileMsg({ type: "error", text: err.response?.data?.detail || "Failed to update profile" });
    }
    setProfileSaving(false);
  };

  const handlePasswordChange = async () => {
    setPwMsg(null);
    if (newPw !== confirmPw) {
      setPwMsg({ type: "error", text: "New passwords don't match" });
      return;
    }
    if (newPw.length < 6) {
      setPwMsg({ type: "error", text: "New password must be at least 6 characters" });
      return;
    }
    setPwSaving(true);
    try {
      await axios.put(`${API}/auth/change-password`, { current_password: currentPw, new_password: newPw }, { withCredentials: true });
      setPwMsg({ type: "success", text: "Password changed successfully" });
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (err) {
      setPwMsg({ type: "error", text: err.response?.data?.detail || "Failed to change password" });
    }
    setPwSaving(false);
  };

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>Settings</h1>

      {/* Profile Section */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
            <User size={16} className="text-indigo-500" />
          </div>
          <h2 className="text-base font-bold" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>Profile</h2>
        </div>

        {/* Avatar */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <span className="text-xl font-bold text-orange-600">{user?.name?.[0] || "U"}</span>
            )}
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">{user?.name || "User"}</p>
            <p className="text-xs text-slate-500">{user?.email}</p>
            <p className="text-[10px] text-indigo-500 font-medium mt-0.5 capitalize">{user?.plan || "starter"} plan</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Full Name</label>
            <div className="flex items-center gap-2 bg-slate-50 rounded-xl border border-slate-200 px-3 py-2.5">
              <User size={14} className="text-slate-400" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="text-sm outline-none flex-1 bg-transparent text-slate-800 placeholder:text-slate-400"
                placeholder="Your name"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Company</label>
            <div className="flex items-center gap-2 bg-slate-50 rounded-xl border border-slate-200 px-3 py-2.5">
              <Building size={14} className="text-slate-400" />
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="text-sm outline-none flex-1 bg-transparent text-slate-800 placeholder:text-slate-400"
                placeholder="Your company or agency"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Email</label>
            <div className="flex items-center gap-2 bg-slate-100 rounded-xl border border-slate-200 px-3 py-2.5">
              <Mail size={14} className="text-slate-400" />
              <span className="text-sm text-slate-500">{user?.email}</span>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Email cannot be changed</p>
          </div>
        </div>

        {profileMsg && (
          <div className={`mt-4 text-xs px-3 py-2 rounded-lg ${profileMsg.type === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
            {profileMsg.text}
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <Button onClick={handleProfileSave} disabled={profileSaving} className="rounded-xl text-sm bg-indigo-600 hover:bg-indigo-700 text-white">
            {profileSaving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Check size={14} className="mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Password Section */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
            <Lock size={16} className="text-amber-500" />
          </div>
          <h2 className="text-base font-bold" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>Change Password</h2>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Current Password</label>
            <div className="flex items-center gap-2 bg-slate-50 rounded-xl border border-slate-200 px-3 py-2.5">
              <Lock size={14} className="text-slate-400" />
              <input
                type="password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                className="text-sm outline-none flex-1 bg-transparent text-slate-800 placeholder:text-slate-400"
                placeholder="Enter current password"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">New Password</label>
            <div className="flex items-center gap-2 bg-slate-50 rounded-xl border border-slate-200 px-3 py-2.5">
              <Lock size={14} className="text-slate-400" />
              <input
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="text-sm outline-none flex-1 bg-transparent text-slate-800 placeholder:text-slate-400"
                placeholder="Enter new password (min 6 characters)"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Confirm New Password</label>
            <div className="flex items-center gap-2 bg-slate-50 rounded-xl border border-slate-200 px-3 py-2.5">
              <Lock size={14} className="text-slate-400" />
              <input
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="text-sm outline-none flex-1 bg-transparent text-slate-800 placeholder:text-slate-400"
                placeholder="Confirm new password"
              />
            </div>
          </div>
        </div>

        {pwMsg && (
          <div className={`mt-4 text-xs px-3 py-2 rounded-lg ${pwMsg.type === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
            {pwMsg.text}
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <Button onClick={handlePasswordChange} disabled={pwSaving || !currentPw || !newPw || !confirmPw} variant="outline" className="rounded-xl text-sm">
            {pwSaving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Lock size={14} className="mr-2" />}
            Change Password
          </Button>
        </div>
      </div>

      {/* Email Verification Banner */}
      {user && !user.email_verified && (
        <EmailVerificationBanner />
      )}

      {/* Marketing & Privacy */}
      <MarketingConsentSection user={user} />

      {/* Security — MFA */}
      <SecuritySection user={user} />

      {/* Plan & Billing */}
      <BillingSection user={user} />

      {/* Data & Account */}
      <DangerZoneSection user={user} />
    </div>
  );
}

function MarketingConsentSection({ user }) {
  const [marketing, setMarketing] = useState(user?.marketing_consent || false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleToggle = async () => {
    const newVal = !marketing;
    setMarketing(newVal);
    setSaving(true);
    try {
      await axios.put(`${API}/auth/consent`, { marketing_consent: newVal }, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
    setSaving(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
          <Mail size={16} className="text-violet-500" />
        </div>
        <h2 className="text-base font-bold" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>Privacy & Communications</h2>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-700">Marketing Communications</p>
            <p className="text-xs text-slate-500 mt-0.5">Receive updates about new features, tips, and promotions</p>
          </div>
          <button onClick={handleToggle} disabled={saving}
            className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${marketing ? "bg-orange-500 justify-end" : "bg-slate-300 justify-start"}`}>
            <div className="w-5 h-5 bg-white rounded-full shadow-sm" />
          </button>
        </div>
        {saved && <p className="text-xs text-emerald-600">Preferences saved.</p>}

        <div className="pt-3 border-t border-slate-100 flex flex-wrap gap-3">
          <a href="/privacy" className="text-xs text-orange-600 hover:underline">Privacy Policy</a>
          <a href="/terms" className="text-xs text-orange-600 hover:underline">Terms and Conditions</a>
          <a href="/cookie-settings" className="text-xs text-orange-600 hover:underline">Cookie Settings</a>
        </div>
      </div>
    </div>
  );
}

function BillingSection({ user }) {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const billingStatus = searchParams.get("billing");

  useEffect(() => {
    const fetchSub = async () => {
      try {
        // First try to sync from Stripe (in case webhook missed)
        await axios.post(`${API}/stripe/sync`, {}, { withCredentials: true });
        // Then fetch the updated subscription info
        const { data } = await axios.get(`${API}/stripe/subscription`, { withCredentials: true });
        setSub(data);
      } catch {}
      setLoading(false);
    };
    fetchSub();
  }, []);

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const { data } = await axios.post(`${API}/stripe/portal`, {}, { withCredentials: true });
      window.location.href = data.url;
    } catch (err) {
      alert(err.response?.data?.detail || "Could not open billing portal");
      setPortalLoading(false);
    }
  };

  const handleChoosePlan = () => {
    navigate("/dashboard?showPlans=true");
  };

  // Auto-sync plan from Stripe when returning from checkout
  useEffect(() => {
    if (billingStatus === "success") {
      const syncPlan = async () => {
        try {
          await axios.post(`${API}/stripe/sync`, {}, { withCredentials: true });
          // Refresh subscription data and user context
          const { data } = await axios.get(`${API}/stripe/subscription`, { withCredentials: true });
          setSub(data);
          await refreshUser();
        } catch (err) {
          console.error("Sync failed:", err);
        }
      };
      // Wait a moment for Stripe webhook to process, then sync
      setTimeout(syncPlan, 2000);
    }
  }, [billingStatus]);

  const plan = sub?.plan || user?.plan || "trial";
  const planNames = { free: "Free", trial: "Trial", starter: "Starter", growth: "Growth", agency: "Agency", enterprise: "Enterprise" };
  const planLimits = { free: 0, trial: 2, starter: 5, growth: 20, agency: 60, enterprise: "Unlimited" };
  const statusColors = { active: "text-emerald-600 bg-emerald-50", trialing: "text-blue-600 bg-blue-50", past_due: "text-red-600 bg-red-50", canceled: "text-slate-500 bg-slate-50" };
  const statusLabels = { active: "Active", trialing: "Trial", past_due: "Past Due", canceled: "Canceled" };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
          <CreditCard size={16} className="text-orange-500" />
        </div>
        <h2 className="text-base font-bold" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>Plan & Billing</h2>
      </div>

      {billingStatus === "success" && (
        <div className="mb-4 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2">
          <Check size={16} className="text-emerald-600" />
          <p className="text-sm text-emerald-700 font-medium">Subscription activated successfully!</p>
        </div>
      )}

      {loading ? (
        <div className="h-20 flex items-center justify-center">
          <Loader2 size={20} className="text-slate-400 animate-spin" />
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {planNames[plan] || "Free"}
                </span>
                {sub?.subscription_status && (
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusColors[sub.subscription_status] || statusColors.active}`}>
                    {statusLabels[sub.subscription_status] || sub.subscription_status}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {planLimits[plan] === 0 ? "No analyses included" : `${planLimits[plan]} analyses / month`}
              </p>
              {sub?.current_period_end && (
                <p className="text-xs text-slate-400 mt-0.5">
                  {sub.subscription_status === "trialing" ? "Trial ends" : "Renews"}: {new Date(sub.current_period_end).toLocaleDateString()}
                </p>
              )}
              {sub?.trial_end && sub.subscription_status === "trialing" && (
                <p className="text-xs text-blue-500 mt-0.5">
                  Free trial until {new Date(sub.trial_end).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {plan !== "free" && plan !== "trial" && sub?.stripe_customer_id && (
                <Button onClick={handleManageBilling} disabled={portalLoading} variant="outline" className="rounded-xl text-sm">
                  {portalLoading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <ExternalLink size={14} className="mr-2" />}
                  Manage Subscription
                </Button>
              )}
              {(plan === "free" || plan === "trial") && (
                <Button onClick={handleChoosePlan} disabled={portalLoading} className="gradient-btn rounded-xl text-sm font-semibold">
                  {portalLoading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />} Choose a Plan
                </Button>
              )}
            </div>
          </div>

          {(plan === "free" || plan === "trial") && (
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <p className="text-sm text-slate-600">{plan === "trial" ? "You're on the free trial with 2 analyses per month. Upgrade to unlock more." : "You're on the free plan. Choose a plan to start analyzing ads."}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function EmailVerificationBanner() {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleResend = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, {}, { withCredentials: true });
      setSent(true);
    } catch {}
    setSending(false);
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
          <Mail size={16} className="text-amber-600" />
        </div>
        <div>
          <p className="text-sm font-medium text-amber-800">Verify your email address</p>
          <p className="text-xs text-amber-600">Check your inbox for a verification link. You need to verify to upgrade your plan.</p>
        </div>
      </div>
      {sent ? (
        <span className="text-xs text-emerald-600 font-medium flex-shrink-0">Sent!</span>
      ) : (
        <Button onClick={handleResend} disabled={sending} variant="outline" className="rounded-xl text-xs flex-shrink-0 border-amber-300 text-amber-700 hover:bg-amber-100">
          {sending ? <Loader2 size={12} className="animate-spin" /> : "Resend"}
        </Button>
      )}
    </div>
  );
}

function SecuritySection({ user }) {
  const [mfaEnabled, setMfaEnabled] = useState(user?.mfa_enabled || false);
  const [showSetup, setShowSetup] = useState(false);
  const [secret, setSecret] = useState("");
  const [qrUri, setQrUri] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDisable, setShowDisable] = useState(false);
  const [disableCode, setDisableCode] = useState("");

  const handleSetup = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/mfa/setup`, {}, { withCredentials: true });
      setSecret(data.secret);
      setQrUri(data.qr_uri);
      setShowSetup(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to set up MFA");
    }
    setLoading(false);
  };

  const handleEnable = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/mfa/enable`, { code }, { withCredentials: true });
      setMfaEnabled(true);
      setShowSetup(false);
      setCode("");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid code");
    }
    setLoading(false);
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/mfa/disable`, { code: disableCode }, { withCredentials: true });
      setMfaEnabled(false);
      setShowDisable(false);
      setDisableCode("");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid code");
    }
    setLoading(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
          <Shield size={16} className="text-emerald-500" />
        </div>
        <h2 className="text-base font-bold" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>Security</h2>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-slate-700">Two-Factor Authentication (2FA)</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {mfaEnabled ? "Enabled — your account is protected with an authenticator app" : "Add an extra layer of security with an authenticator app"}
          </p>
        </div>
        {mfaEnabled ? (
          <Button onClick={() => setShowDisable(true)} variant="outline" className="rounded-xl text-xs border-red-200 text-red-600 hover:bg-red-50">Disable 2FA</Button>
        ) : (
          <Button onClick={handleSetup} disabled={loading} className="gradient-btn rounded-xl text-xs font-semibold">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <><Smartphone size={12} className="mr-1" /> Enable 2FA</>}
          </Button>
        )}
      </div>

      {error && <p className="text-xs text-red-500 mt-2">{error}</p>}

      {/* Setup modal */}
      {showSetup && (
        <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
          <h4 className="text-sm font-bold text-slate-800 mb-2">Set up authenticator</h4>
          <p className="text-xs text-slate-500 mb-3">Scan this QR code with Google Authenticator, Authy, or any TOTP app:</p>
          <div className="bg-white p-4 rounded-xl border border-slate-200 mb-3 text-center">
            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUri)}`} alt="QR Code" className="w-48 h-48 mx-auto" />
          </div>
          <p className="text-[10px] text-slate-400 mb-3 break-all">Manual key: <code className="bg-slate-100 px-1 py-0.5 rounded text-[10px]">{secret}</code></p>
          <form onSubmit={handleEnable} className="flex gap-2">
            <input type="text" placeholder="Enter 6-digit code" value={code} onChange={e => setCode(e.target.value)} maxLength={6}
              className="flex-1 h-9 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-orange-400" />
            <Button type="submit" disabled={loading || code.length !== 6} className="gradient-btn rounded-lg text-xs h-9 px-4">
              {loading ? <Loader2 size={12} className="animate-spin" /> : "Verify & Enable"}
            </Button>
          </form>
        </div>
      )}

      {/* Disable modal */}
      {showDisable && (
        <div className="mt-4 p-4 bg-red-50 rounded-xl border border-red-200">
          <h4 className="text-sm font-bold text-red-800 mb-2">Disable 2FA</h4>
          <p className="text-xs text-red-600 mb-3">Enter a code from your authenticator app to disable 2FA:</p>
          <form onSubmit={handleDisable} className="flex gap-2">
            <input type="text" placeholder="6-digit code" value={disableCode} onChange={e => setDisableCode(e.target.value)} maxLength={6}
              className="flex-1 h-9 px-3 rounded-lg border border-red-200 text-sm outline-none focus:border-red-400" />
            <Button type="submit" disabled={loading || disableCode.length !== 6} variant="outline" className="rounded-lg text-xs h-9 px-4 border-red-300 text-red-600">
              {loading ? <Loader2 size={12} className="animate-spin" /> : "Disable"}
            </Button>
            <Button type="button" onClick={() => { setShowDisable(false); setDisableCode(""); }} variant="ghost" className="rounded-lg text-xs h-9 px-3 text-slate-500">Cancel</Button>
          </form>
        </div>
      )}
    </div>
  );
}

function DangerZoneSection({ user }) {
  const [exporting, setExporting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await axios.get(`${API}/auth/export-data`, { withCredentials: true });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `adbit_data_export_${new Date().toISOString().slice(0,10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("Export failed. Please try again.");
    }
    setExporting(false);
  };

  const handleDelete = async () => {
    if (deleteConfirm !== "DELETE") return;
    setDeleting(true);
    setDeleteError("");
    try {
      await axios.post(`${API}/auth/delete-account`, { confirmation: "DELETE" }, { withCredentials: true });
      window.location.href = "/";
    } catch (err) {
      setDeleteError(err.response?.data?.detail || "Deletion failed");
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
          <AlertTriangle size={16} className="text-slate-500" />
        </div>
        <h2 className="text-base font-bold" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>Data & Account</h2>
      </div>

      <div className="space-y-4">
        {/* Data Export */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-700">Export Your Data</p>
            <p className="text-xs text-slate-500 mt-0.5">Download all your account data and analyses as JSON</p>
          </div>
          <Button onClick={handleExport} disabled={exporting} variant="outline" className="rounded-xl text-xs">
            {exporting ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Download size={12} className="mr-1" />}
            Export Data
          </Button>
        </div>

        {/* Delete Account */}
        <div className="pt-4 border-t border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-red-600">Delete Account</p>
              <p className="text-xs text-slate-500 mt-0.5">Permanently delete your account, analyses, and all associated data</p>
            </div>
            <Button onClick={() => setShowDelete(true)} variant="outline" className="rounded-xl text-xs border-red-200 text-red-600 hover:bg-red-50">
              <Trash2 size={12} className="mr-1" /> Delete Account
            </Button>
          </div>

          {showDelete && (
            <div className="mt-4 p-4 bg-red-50 rounded-xl border border-red-200">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={16} className="text-red-500" />
                <h4 className="text-sm font-bold text-red-800">This action is permanent</h4>
              </div>
              <p className="text-xs text-red-600 mb-3">All your analyses, results, team memberships, and account data will be permanently deleted. Your subscription will be cancelled. This cannot be undone.</p>
              <p className="text-xs text-slate-600 mb-2">Type <strong>DELETE</strong> to confirm:</p>
              <div className="flex gap-2">
                <input type="text" value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} placeholder="DELETE"
                  className="flex-1 h-9 px-3 rounded-lg border border-red-200 text-sm outline-none focus:border-red-400 font-mono" />
                <Button onClick={handleDelete} disabled={deleteConfirm !== "DELETE" || deleting} variant="outline" className="rounded-lg text-xs h-9 px-4 border-red-300 text-red-600 hover:bg-red-100">
                  {deleting ? <Loader2 size={12} className="animate-spin" /> : "Confirm Delete"}
                </Button>
                <Button onClick={() => { setShowDelete(false); setDeleteConfirm(""); }} variant="ghost" className="rounded-lg text-xs h-9 px-3 text-slate-500">Cancel</Button>
              </div>
              {deleteError && <p className="text-xs text-red-500 mt-2">{deleteError}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
